// CandidatePoolPanel.jsx
// Embedded inside each RoleAccordionSection — renders the 3-slot candidate grid.
import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Autocomplete,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Search as SearchIcon, Refresh as RefreshIcon, Close as CloseIcon } from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CandidateTile from './CandidateTile';

export default function CandidatePoolPanel({
  role,
  staffingData,
  rolePool,
  allTechFitOpen,
  setAllTechFitOpen,
  allEmployees,
  onViewDetails,
  onAnalyzeChemistry,
  onSelectSearchCandidate,
  onClearSearchCandidate,
  onFindCandidates,
  assignCandidateToRole,
  unassignCandidateFromRole,
}) {
  const { candidates = [], commentary = '', isSearching = false, searchCandidate = null } = rolePool || {};

  const liveRole = role
    ? (staffingData?.roles?.find(r => r.id === role.id) || role)
    : null;

  if (isSearching) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>
          Concierge is Identifying Candidates...
        </Typography>
      </Box>
    );
  }

  if (candidates.length === 0) {
    const emptyMsg = !role
      ? 'No role selected'
      : role.status === 'assigned'
        ? 'Click Remove to search for other candidates for this role'
        : 'Click Find to search for candidates for this role';
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body1" color="text.secondary">
          {emptyMsg}
        </Typography>
      </Box>
    );
  }

  const poolCandidates = candidates.slice(0, 3);
  const poolIds = new Set(candidates.map(c => c.employee_id));
  const searchableEmployees = allEmployees.filter(emp => !poolIds.has(emp.employee_id));

  return (
    <Box>
      {/* Commentary + get-more refresh */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        {commentary && (
          <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', flexGrow: 1 }}>
            {commentary}
          </Typography>
        )}
        {onFindCandidates && !isSearching && (
          <Tooltip title="Get more options (won't repeat shown candidates)">
            <IconButton
              size="small"
              onClick={() => onFindCandidates(role)}
              sx={{ ml: 1, flexShrink: 0, color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* 3-slot candidate grid */}
      <Grid container spacing={2}>
        {poolCandidates.map((candidate) => (
          <Grid item xs={12} sm={6} md={3} key={candidate.employee_id}>
            <CandidateTile
              candidate={candidate}
              onViewDetails={onViewDetails}
              onAnalyzeChemistry={onAnalyzeChemistry ? (c) => onAnalyzeChemistry(c, role) : null}
              onAssign={liveRole && liveRole.assigned_employee_id !== candidate.employee_id ? () => assignCandidateToRole(liveRole.id, candidate) : null}
              onUnassign={liveRole && liveRole.assigned_employee_id === candidate.employee_id ? () => unassignCandidateFromRole(liveRole.id) : null}
              isAssignedToRole={!!(liveRole && liveRole.assigned_employee_id === candidate.employee_id)}
              availabilityLabel={candidate.availability_label}
              riskLevel={candidate.risk_level}
              techFitOpen={allTechFitOpen}
              onTechFitToggle={() => setAllTechFitOpen(v => !v)}
            />
          </Grid>
        ))}

        {/* "+" manual-add slot */}
        <Grid item xs={12} sm={6} md={3}>
          {searchCandidate ? (
            <Box sx={{ position: 'relative' }}>
              <Tooltip title="Remove and try someone else">
                <IconButton
                  size="small"
                  onClick={() => onClearSearchCandidate && onClearSearchCandidate(role)}
                  sx={{
                    position: 'absolute', top: 4, right: 4, zIndex: 1,
                    bgcolor: 'background.paper', border: '1px solid rgba(255,255,255,0.2)',
                    width: 20, height: 20,
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <CloseIcon sx={{ fontSize: '0.75rem' }} />
                </IconButton>
              </Tooltip>
              <CandidateTile
                candidate={searchCandidate}
                onViewDetails={onViewDetails}
                onAnalyzeChemistry={onAnalyzeChemistry ? (c) => onAnalyzeChemistry(c, role) : null}
                onAssign={liveRole && liveRole.assigned_employee_id !== searchCandidate.employee_id ? () => assignCandidateToRole(liveRole.id, searchCandidate) : null}
                onUnassign={liveRole && liveRole.assigned_employee_id === searchCandidate.employee_id ? () => unassignCandidateFromRole(liveRole.id) : null}
                isAssignedToRole={!!(liveRole && liveRole.assigned_employee_id === searchCandidate.employee_id)}
                availabilityLabel={searchCandidate.availability_label}
                riskLevel={searchCandidate.risk_level}
                techFitOpen={allTechFitOpen}
                onTechFitToggle={() => setAllTechFitOpen(v => !v)}
              />
            </Box>
          ) : (
            <Box
              sx={{
                border: '1px dashed rgba(255,255,255,0.25)',
                borderRadius: 1,
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1.5,
                minHeight: 120
              }}
            >
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Add candidate manually
              </Typography>
              <Autocomplete
                options={searchableEmployees}
                getOptionLabel={(option) => option.name}
                value={null}
                onChange={(event, newValue) => {
                  if (newValue && onSelectSearchCandidate) {
                    onSelectSearchCandidate(newValue, role);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Search by name..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <SearchIcon sx={{ fontSize: '1rem' }} />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                sx={{
                  width: '100%',
                  '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' }
                }}
              />
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}

CandidatePoolPanel.propTypes = {
  role: PropTypes.object,
  staffingData: PropTypes.object,
  rolePool: PropTypes.shape({
    candidates: PropTypes.array,
    commentary: PropTypes.string,
    isSearching: PropTypes.bool,
    searchCandidate: PropTypes.object,
  }),
  allTechFitOpen: PropTypes.bool,
  setAllTechFitOpen: PropTypes.func.isRequired,
  allEmployees: PropTypes.array.isRequired,
  onViewDetails: PropTypes.func.isRequired,
  onAnalyzeChemistry: PropTypes.func,
  onSelectSearchCandidate: PropTypes.func.isRequired,
  onClearSearchCandidate: PropTypes.func,
  onFindCandidates: PropTypes.func,
  assignCandidateToRole: PropTypes.func.isRequired,
  unassignCandidateFromRole: PropTypes.func.isRequired,
};
